import React from "react";

function apptBgColor(appt) {
  if (appt.status === "cancelled") return "bg-red-500";
  if (appt.status === "attended") return "bg-gray-400";
  if (appt.status === "dna") return "bg-orange-500";
  
  // Enhanced styling for different OOO event types
  if (appt.type === "ooo" || appt.type === "event") {
    // Leave requests - different colors based on status (muted tones)
    if (appt.eventType === "leave" || appt.eventType === "annual_leave") {
      return appt.status === "pending" ? "bg-amber-200" : "bg-emerald-200";
    }
    // Sick leave
    if (appt.eventType === "sick" || appt.eventType === "sick_leave") {
      return appt.status === "pending" ? "bg-orange-200" : "bg-red-200";
    }
    // Training/Conference
    if (appt.eventType === "training" || appt.eventType === "conference") {
      return "bg-indigo-600";
    }
    // Personal/Other
    if (appt.eventType === "personal" || appt.eventType === "other") {
      return "bg-purple-600";
    }
    // Block time / general OOO
    return "bg-teal-200";
  }
  
  return "bg-blue-500"; // Regular appointments
}

function apptTextColor(appt) {
  if (appt.status === "cancelled") return "text-white";
  if (appt.status === "attended") return "text-white";
  if (appt.status === "dna") return "text-white";

  // Enhanced text colors for different OOO event types
  if (appt.type === "ooo" || appt.type === "event") {
    // Light backgrounds need dark text
    if (appt.eventType === "leave" || appt.eventType === "annual_leave") {
      return "text-gray-800";
    }
    // Sick leave
    if (appt.eventType === "sick" || appt.eventType === "sick_leave") {
      return "text-gray-800";
    }
    // Training/Conference (dark background)
    if (appt.eventType === "training" || appt.eventType === "conference") {
      return "text-white";
    }
    // Personal/Other (dark background)
    if (appt.eventType === "personal" || appt.eventType === "other") {
      return "text-white";
    }
    // Block time / general OOO (light background)
    return "text-gray-800";
  }

  return "text-white"; // Regular appointments
}

// HOURS will be passed as prop from parent component

// Minimal NoteStatus dot for reuse
function NoteStatus({ status, onClick, title }) {
  const map = {
    red: "bg-red-500",
    amber: "bg-yellow-400",
    green: "bg-green-500",
  };
  return (
    <span
      className={`inline-block w-3 h-3 rounded-full border border-white cursor-pointer ${map[status] || map.red} hover:scale-125 transition`}
      title={title}
      onClick={e => {
        e.stopPropagation();
        if (onClick) onClick();
      }}
      tabIndex={0}
      role={onClick ? "button" : undefined}
    ></span>
  );
}

export default function DayView({
  date,
  appointments,
  hours = [], // Dynamic hours from business settings
  onSlotClick,
  onEdit,
  getNoteStatus = () => "red",
  getInvoiceStatus = () => "red",
  handleSoapDotClick,
  handleInvoiceDotClick
}) {
  // Use timezone-safe date formatting to avoid UTC conversion issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const isoDay = `${year}-${month}-${day}`;

  return (
    <div className="w-full overflow-y-auto px-4">
      {/* HEADER ROW */}
      <div className="grid grid-cols-[80px_1fr] border-b">
        <div className="bg-gray-100 p-2 font-semibold text-center">Time</div>
        <div className="bg-gray-100 p-2 font-semibold text-center">Appointments</div>
      </div>
      {/* SLOTS */}
      <div className="grid grid-cols-[80px_1fr]">
        {hours.map((hour, i) => {
          const slotAppts = appointments.filter((a) => {
            try {
              // Check if appointment has valid data
              if (!a.date || !a.time) {
                console.warn('Appointment missing date or time:', a);
                return false;
              }
              
              // More flexible date matching with error handling
              const appointmentDateObj = new Date(a.date);
              if (isNaN(appointmentDateObj.getTime())) {
                console.warn('Invalid appointment date:', a.date, 'for appointment:', a);
                return false;
              }
              
              const appointmentDate = appointmentDateObj.toISOString().slice(0, 10);
              const dateMatches = appointmentDate === isoDay;
              
              // Debug logging for date comparison
              if (i === 0) { // Only log once per day
                console.log(`🔍 DayView Debug: Comparing dates for ${appointments.length} appointments`);
                console.log(`📅 Target date (isoDay): ${isoDay}`);
                console.log(`📅 Sample appointment date: ${a.date} → ${appointmentDate}`);
                console.log(`✅ Date matches: ${dateMatches}`);
              }
              
              // For all-day OOO events (either marked as allDay or time-block events with 00:00-23:59)
              const isAllDayEvent = (a.type === 'ooo' || a.type === 'event') && 
                (a.allDay || (a.time === '00:00' && a.endTime === '23:59'));
              
              if (isAllDayEvent) {
                return dateMatches && i === 0; // Show in first time slot
              }
              
              // Match by time instead of startIndex (since appointments use time property)
              const timeMatches = a.time === hour;
              
              return dateMatches && timeMatches;
            } catch (error) {
              console.error('Error processing appointment in day view:', error, a);
              return false;
            }
          });
          return (
            <React.Fragment key={hour}>
              {/* time label cell */}
              <div
                className="border border-gray-200 text-sm text-center bg-gray-50"
                style={{ height: 32, lineHeight: "32px" }}
              >
                {i % 2 === 0 ? hour : ""}
              </div>
              {/* clickable appointment cell */}
              <div
                className="relative border border-gray-200 h-[32px] cursor-pointer hover:bg-blue-50"
                onClick={() =>
                  onSlotClick({ date, hour, startIndex: i })
                }
              >
                {slotAppts.map((appt, idx) => {
                  const rows = Math.ceil(appt.duration / 30);
                  const noteStatus = getNoteStatus(appt);
                  const invoiceStatus = getInvoiceStatus(appt);
                  return (
                    <div
                      key={appt.id}
                      className={`absolute top-0 left-0 ${apptTextColor(appt)} text-xs p-1 rounded shadow z-10 overflow-hidden flex flex-col items-start ${apptBgColor(appt)} ${(appt.type === 'ooo' || appt.type === 'event') ? 'border-2 border-gray-300/50' : ''}`}
                      style={{
                        height: `${rows * 32}px`,
                        width: `${100 / slotAppts.length}%`,
                        left: `${(100 / slotAppts.length) * idx}%`,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(appt, {
                          date,
                          hour,
                          startIndex: i,
                        });
                      }}
                    >
                      <div className="font-bold text-[13px] leading-tight">
                        {appt.type === 'ooo' || appt.type === 'event' ? (
                          <div className="flex items-center gap-1">
                            <span className="truncate">{appt.title || appt.eventType || 'OOO Event'}</span>
                            {appt.status === 'pending' && (
                              <span className="text-[8px] bg-white/30 px-1 rounded font-medium">PENDING</span>
                            )}
                          </div>
                        ) : (
                          appt.client?.name || "Unknown"
                        )}
                      </div>
                      <div className="text-[10px] leading-tight">
                        {appt.type === 'ooo' || appt.type === 'event' ? (
                          <div className="flex items-center justify-between gap-1">
                            <span>{appt.allDay ? 'All Day' : `${appt.duration || 60} mins`}</span>
                            {appt.eventType && (
                              <span className="text-[8px] bg-white/20 px-1 rounded uppercase font-medium">
                                {appt.eventType.replace('_', ' ')}
                              </span>
                            )}
                          </div>
                        ) : (
                          `${(appt.hour || "")} ${appt.type} ${appt.duration} mins`
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        {appt.status === "cancelled" && (
                          <span title="Cancelled" className="text-xs mr-1">✖️</span>
                        )}
                        {appt.status === "dna" && (
                          <span title="Did Not Attend" className="text-xs mr-1">DNA</span>
                        )}
                        {appt.status === "attended" && (
                          <span title="Attended" className="text-xs mr-1">✔️</span>
                        )}
                        {/* Only show SOAP and invoice status for regular appointments, not OOO events */}
                        {appt.type !== 'ooo' && appt.type !== 'event' && (
                          <>
                            <NoteStatus
                              status={noteStatus}
                              onClick={() => handleSoapDotClick(appt, noteStatus)}
                              title={
                                noteStatus === "green"
                                  ? "View finalised SOAP note"
                                  : noteStatus === "amber"
                                    ? "Edit SOAP note"
                                    : "Create SOAP note"
                              }
                            />
                            <NoteStatus
                              status={invoiceStatus}
                              onClick={() => handleInvoiceDotClick(appt)}
                              title={
                                invoiceStatus === "green"
                                  ? "Invoice paid"
                                  : invoiceStatus === "amber"
                                    ? "Invoice created - unpaid"
                                    : "Create invoice"
                              }
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
